/**
 * The Revalidation Summary's columns, headings, cell values and row selection.
 *
 * Deliberately a mirror of REVALIDATION_* and revalidationColumns /
 * revalidationLabels / revalidationValue / revalidationRows / revalidationCounts
 * in backend/utils/report-helpers.js, as walkthroughColumns.js and
 * tableLabelDefaults.js already mirror theirs. verify-report-bands.js compares
 * the two across a config matrix, so a change here that is not made there is a
 * failing check rather than an editor that quietly lies about the document.
 *
 * KEYS is also the order columns print in, which is what lets a column ticked
 * later land in its proper place rather than on the end of the list.
 */

export const REVALIDATION_COLUMN_KEYS = ['num', 'uid', 'title', 'originalRisk', 'originalStatus', 'newStatus'];
export const REVALIDATION_DEFAULT_COLUMNS = ['num', 'title', 'originalRisk', 'originalStatus', 'newStatus'];

export const REVALIDATION_COLUMN_LABELS = {
  num: '#', uid: 'ID', title: 'Finding',
  originalRisk: 'Original Risk', originalStatus: 'Original Status', newStatus: 'New Status',
};

export const REVALIDATION_LABEL_KEYS = {
  num: 'labelNum', uid: 'labelUid', title: 'labelTitle',
  originalRisk: 'labelOriginalRisk', originalStatus: 'labelOriginalStatus', newStatus: 'labelNewStatus',
};

/**
 * What each finding status is coloured. Closed is the only green: Accepted and
 * Reopened both mean the issue is still there.
 */
export const REVALIDATION_STATUS_COLORS = {
  Open: '#c0392b', Reopened: '#a93226', Retest: '#b7791f',
  Accepted: '#6b7280', Closed: '#1e8449',
};

/** The columns this table prints, in canonical order. */
export function revalidationColumns(config) {
  const given = (config || {}).columns;
  const picked = Array.isArray(given) && given.length
    ? REVALIDATION_COLUMN_KEYS.filter(k => given.includes(k))
    : [];
  return picked.length ? picked : [...REVALIDATION_DEFAULT_COLUMNS];
}

/** Every column's heading, the template's where it set one. */
export function revalidationLabels(config) {
  const cfg = config || {};
  const out = {};
  for (const k of REVALIDATION_COLUMN_KEYS) {
    const given = cfg[REVALIDATION_LABEL_KEYS[k]];
    out[k] = typeof given === 'string' ? given : REVALIDATION_COLUMN_LABELS[k];
  }
  return out;
}

/** One cell's text. Every retest column falls back to the live value. */
export function revalidationValue(finding, key) {
  const f = finding || {};
  switch (key) {
    case 'uid':            return f.finding_uid || '';
    case 'title':          return f.title || '';
    case 'originalRisk':   return f.original_severity || f.severity || '';
    case 'originalStatus': return f.original_status || f.status || '';
    case 'newStatus':      return f.retest_status || f.status || '';
    default:               return '';
  }
}

/** The findings this table lists. */
export function revalidationRows(findings, config) {
  const cfg = config || {};
  const all = Array.isArray(findings) ? findings : [];
  const snapshot = all.filter(f => f && f.original_severity);
  let rows = snapshot.length ? snapshot : all;

  if (!cfg.includeOriginallyClosed) {
    rows = rows.filter(f => revalidationValue(f, 'originalStatus') !== 'Closed');
  }

  const sev = cfg.severityFilter;
  if (Array.isArray(sev) && sev.length) {
    rows = rows.filter(f => sev.includes(revalidationValue(f, 'originalRisk')));
  }

  const max = parseInt(cfg.maxRows, 10);
  if (Number.isFinite(max) && max > 0) rows = rows.slice(0, max);
  return rows;
}

/** How many findings were revalidated, and how many ended closed. */
export function revalidationCounts(findings) {
  const snapshot = (Array.isArray(findings) ? findings : []).filter(f => f && f.original_severity);
  const rows = snapshot.filter(f => revalidationValue(f, 'originalStatus') !== 'Closed');
  const closed = rows.filter(f => revalidationValue(f, 'newStatus') === 'Closed').length;
  return { total: rows.length, closed, open: rows.length - closed };
}
